import { useState, useEffect } from 'react';
import JSZip from 'jszip';
import { X, FileText, FolderOpen, Image, File, AlertCircle, Archive, Loader2, Upload, Tag } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

interface SkillZipImportDialogProps {
  file: File;
  onClose: () => void;
  onConfirm: (file: File, skillName: string) => Promise<void>;
}

interface SkillMetadata {
  name?: string;
  description?: string;
  version?: string;
  category?: string;
  tags?: string[];
}

interface FileEntry {
  name: string;
  path: string;
  size: number;
  isDirectory: boolean;
  depth: number;
}

type DialogState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'preview'; metadata: SkillMetadata; files: FileEntry[]; totalSize: number; fileCount: number }
  | { kind: 'importing'; metadata: SkillMetadata; files: FileEntry[]; totalSize: number; fileCount: number };

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function parseFrontmatter(content: string): SkillMetadata {
  const match = content.match(/^---\n([\s\S]*?)\n---/);
  if (!match) return {};

  const metadata: SkillMetadata = {};
  const lines = match[1].split('\n');

  for (const line of lines) {
    const colonIndex = line.indexOf(':');
    if (colonIndex > -1) {
      const key = line.substring(0, colonIndex).trim().toLowerCase();
      const value = line.substring(colonIndex + 1).trim().replace(/^["']|["']$/g, '');

      if (key === 'name') metadata.name = value;
      else if (key === 'description') metadata.description = value;
      else if (key === 'version') metadata.version = value;
      else if (key === 'category') metadata.category = value;
      else if (key === 'tags') {
        metadata.tags = value.split(',').map(t => t.trim()).filter(Boolean);
      }
    }
  }

  return metadata;
}

function getFileIcon(entry: FileEntry) {
  if (entry.isDirectory) return <FolderOpen className="size-3.5 text-primary/70 shrink-0" />;
  const ext = entry.name.split('.').pop()?.toLowerCase();
  if (ext && ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'ico'].includes(ext)) {
    return <Image className="size-3.5 text-purple-500/70 shrink-0" />;
  }
  if (ext && ['md', 'txt', 'json', 'yaml', 'yml'].includes(ext)) {
    return <FileText className="size-3.5 text-orange-500/70 shrink-0" />;
  }
  return <File className="size-3.5 text-muted-foreground shrink-0" />;
}

async function buildFileTree(zip: JSZip): Promise<FileEntry[]> {
  const entries: FileEntry[] = [];
  const seenDirs = new Set<string>();

  const sizes = new Map<string, number>();
  const sizePromises: Promise<void>[] = [];
  zip.forEach((relativePath, zipEntry) => {
    if (!zipEntry.dir) {
      sizePromises.push(
        zipEntry.async('uint8array').then(data => { sizes.set(relativePath, data.byteLength); })
      );
    }
  });
  await Promise.all(sizePromises);

  zip.forEach((relativePath, zipEntry) => {
    const parts = relativePath.split('/').filter(Boolean);
    for (let i = 1; i < parts.length; i++) {
      const dirPath = parts.slice(0, i).join('/') + '/';
      if (!seenDirs.has(dirPath)) {
        seenDirs.add(dirPath);
        entries.push({
          name: parts[i - 1],
          path: dirPath,
          size: 0,
          isDirectory: true,
          depth: i - 1,
        });
      }
    }

    if (zipEntry.dir) {
      if (!seenDirs.has(relativePath)) {
        seenDirs.add(relativePath);
        const name = parts[parts.length - 1];
        entries.push({ name, path: relativePath, size: 0, isDirectory: true, depth: parts.length - 1 });
      }
    } else {
      const name = parts[parts.length - 1];
      entries.push({
        name,
        path: relativePath,
        size: sizes.get(relativePath) ?? 0,
        isDirectory: false,
        depth: parts.length - 1,
      });
    }
  });

  entries.sort((a, b) => {
    if (a.path === b.path) return 0;
    const aParts = a.path.split('/').filter(Boolean);
    const bParts = b.path.split('/').filter(Boolean);
    const minLen = Math.min(aParts.length, bParts.length);

    for (let i = 0; i < minLen; i++) {
      if (aParts[i] !== bParts[i]) {
        const aIsDir = i < aParts.length - 1 || a.isDirectory;
        const bIsDir = i < bParts.length - 1 || b.isDirectory;
        if (aIsDir && !bIsDir) return -1;
        if (!aIsDir && bIsDir) return 1;
        return aParts[i].localeCompare(bParts[i]);
      }
    }
    return aParts.length - bParts.length;
  });

  const seen = new Set<string>();
  return entries.filter(e => {
    if (seen.has(e.path)) return false;
    seen.add(e.path);
    return true;
  });
}

export function SkillZipImportDialog({ file, onClose, onConfirm }: SkillZipImportDialogProps) {
  const [state, setState] = useState<DialogState>({ kind: 'loading' });
  const [importError, setImportError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function analyze() {
      try {
        const zip = await JSZip.loadAsync(file);

        let skillMdEntry: JSZip.JSZipObject | null = null;
        zip.forEach((relativePath, entry) => {
          if (!entry.dir && /^([^/]+\/)?SKILL\.md$/i.test(relativePath)) {
            skillMdEntry = entry;
          }
        });

        if (!skillMdEntry) {
          if (!cancelled) setState({ kind: 'error', message: 'No SKILL.md found. A valid skill package must contain a SKILL.md file at the root or one level deep.' });
          return;
        }

        const skillMdContent = await (skillMdEntry as JSZip.JSZipObject).async('string');
        const metadata = parseFrontmatter(skillMdContent);
        const files = await buildFileTree(zip);

        let fileCount = 0;
        let totalSize = 0;
        files.forEach(f => {
          if (!f.isDirectory) {
            fileCount++;
            totalSize += f.size;
          }
        });

        if (!cancelled) {
          setState({ kind: 'preview', metadata, files, totalSize, fileCount });
        }
      } catch {
        if (!cancelled) {
          setState({ kind: 'error', message: 'Failed to read ZIP file. The file may be corrupted or not a valid ZIP archive.' });
        }
      }
    }

    analyze();
    return () => { cancelled = true; };
  }, [file]);

  const handleConfirm = async () => {
    if (state.kind !== 'preview') return;
    const skillName = state.metadata.name || file.name.replace(/\.zip$/i, '');

    setImportError(null);
    const prevState = state;
    setState({ kind: 'importing', metadata: state.metadata, files: state.files, totalSize: state.totalSize, fileCount: state.fileCount });

    try {
      await onConfirm(file, skillName);
    } catch (err) {
      setState(prevState);
      setImportError(err instanceof Error ? err.message : 'Import failed. Please try again.');
    }
  };

  const isImporting = state.kind === 'importing';
  const previewData = state.kind === 'preview' || state.kind === 'importing' ? state : null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-lg w-full max-w-md max-h-[85vh] overflow-hidden flex flex-col border">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center size-8 rounded-lg bg-primary/10">
              <Upload className="size-4 text-primary" />
            </div>
            <div>
              <h2 className="text-sm font-semibold leading-tight">Import Skill</h2>
              <p className="text-xs text-muted-foreground">Review before importing</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isImporting} className="h-7 w-7 p-0">
            <X className="size-4" />
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {state.kind === 'loading' && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="size-6 text-primary animate-spin mb-3" />
              <p className="text-sm text-muted-foreground">Analyzing ZIP contents...</p>
            </div>
          )}

          {state.kind === 'error' && (
            <div className="flex flex-col items-center justify-center py-16 px-6">
              <div className="flex items-center justify-center size-12 rounded-2xl bg-destructive/10 mb-4">
                <AlertCircle className="size-6 text-destructive" />
              </div>
              <p className="text-sm font-medium mb-1">Invalid Package</p>
              <p className="text-xs text-muted-foreground text-center max-w-[280px]">{state.message}</p>
            </div>
          )}

          {previewData && (
            <div className="px-5 py-4 space-y-4">
              {importError && (
                <div className="flex items-start gap-2 bg-destructive/10 text-destructive text-xs p-3 rounded-md border border-destructive/20">
                  <AlertCircle className="size-3.5 shrink-0 mt-0.5" />
                  <span>{importError}</span>
                </div>
              )}

              {/* File summary pill */}
              <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-muted/60 border">
                <Archive className="size-4 text-muted-foreground shrink-0" />
                <span className="text-xs font-medium truncate">{file.name}</span>
                <span className="text-[10px] text-muted-foreground ml-auto shrink-0">
                  {formatSize(previewData.totalSize)} &middot; {previewData.fileCount} file{previewData.fileCount !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Metadata */}
              <div className="space-y-3">
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Skill Details</p>
                <div className="rounded-md border bg-card overflow-hidden">
                  <div className="px-3 py-2.5 border-b last:border-b-0">
                    <p className="text-[10px] text-muted-foreground mb-0.5">Name</p>
                    <p className="text-sm font-medium">{previewData.metadata.name || file.name.replace(/\.zip$/i, '')}</p>
                  </div>
                  {previewData.metadata.description && (
                    <div className="px-3 py-2.5 border-b last:border-b-0">
                      <p className="text-[10px] text-muted-foreground mb-0.5">Description</p>
                      <p className="text-xs text-foreground leading-relaxed">{previewData.metadata.description}</p>
                    </div>
                  )}
                  {(previewData.metadata.version || previewData.metadata.category) && (
                    <div className="flex divide-x border-b last:border-b-0">
                      {previewData.metadata.version && (
                        <div className="flex-1 px-3 py-2.5">
                          <p className="text-[10px] text-muted-foreground mb-0.5">Version</p>
                          <p className="text-xs font-mono">{previewData.metadata.version}</p>
                        </div>
                      )}
                      {previewData.metadata.category && (
                        <div className="flex-1 px-3 py-2.5">
                          <p className="text-[10px] text-muted-foreground mb-0.5">Category</p>
                          <p className="text-xs">{previewData.metadata.category}</p>
                        </div>
                      )}
                    </div>
                  )}
                  {previewData.metadata.tags && previewData.metadata.tags.length > 0 && (
                    <div className="px-3 py-2.5">
                      <p className="text-[10px] text-muted-foreground mb-1.5">Tags</p>
                      <div className="flex flex-wrap gap-1">
                        {previewData.metadata.tags.map((tag) => (
                          <Badge key={tag} variant="secondary" className="text-[10px] px-1.5 py-0 gap-0.5">
                            <Tag className="size-2.5" />
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* File tree */}
              <div className="space-y-3">
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Contents</p>
                <div className="rounded-md border bg-card overflow-hidden">
                  <div className="max-h-40 overflow-auto py-1">
                    {previewData.files.map((entry) => (
                      <div
                        key={entry.path}
                        className="flex items-center gap-1.5 px-3 py-1 text-xs hover:bg-muted/40 transition-colors"
                        style={{ paddingLeft: 12 + entry.depth * 16 }}
                      >
                        {getFileIcon(entry)}
                        <span className={entry.isDirectory ? 'font-medium text-foreground' : 'text-foreground'}>
                          {entry.name}{entry.isDirectory ? '/' : ''}
                        </span>
                        {!entry.isDirectory && entry.size > 0 && (
                          <span className="text-[10px] text-muted-foreground ml-auto tabular-nums shrink-0">
                            {formatSize(entry.size)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t bg-muted/30">
          {state.kind === 'error' && (
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          )}
          {previewData && (
            <>
              <Button variant="outline" size="sm" onClick={onClose} disabled={isImporting}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleConfirm} disabled={isImporting}>
                {isImporting ? (
                  <>
                    <Loader2 className="size-3.5 mr-1.5 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="size-3.5 mr-1.5" />
                    Import Skill
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
